import {
  sortBySalesLift,
  SubdimensionsByName,
  subdimensionsByName,
  topDimensionsByNameSorted,
} from '@features/reports/subdomains/cpg';
import {
  CpgRetailer,
  getTopRetailers,
  useCampaign,
  useCurrentAdSpend,
  useReport,
  UseReportsProps,
} from '@features/campaigns/subdomains/cpg';
import {
  Campaign,
  CpgReport,
  CpgReportsParams,
  GetCampaignParams,
  useReturnOnAdSpend,
} from '@features/campaigns';
import { BuildReportOptions } from '../utils/build-presentation';
import { SubdimensionCounts, validDimensionSlug } from '@features/reports';
import { useMemo } from 'react';

type FetchParams = {
  reportParams: Omit<CpgReportsParams, 'campaignId'>;
  campaignParams: GetCampaignParams;
};

export type UseCpgExecutiveReportFieldsHook = {
  loading: boolean;
  data?: {
    campaign: Pick<Campaign, 'name' | 'startDate' | 'endDate' | 'adSpend'>;
    report: CpgReport;
    subdimensionCounts: SubdimensionCounts;
    roasData: BuildReportOptions['roasData'];
    topSalesSubdimensionsByName: SubdimensionsByName;
    topRetailers: CpgRetailer[];
    retailerCount: number;
  };
  error?: string;
};

type CalculatedValues = {
  topSalesSubdimensionsByName: SubdimensionsByName;
  topRetailers: CpgRetailer[];
  subdimensionCounts: SubdimensionCounts;
};

export const useExecutiveReportFields = (
  fetchParams: FetchParams
): UseCpgExecutiveReportFieldsHook => {
  const { campaign, isFetching: loadingCampaign } = useCampaign(
    fetchParams.campaignParams
  );

  const reportsProps: UseReportsProps = {
    campaignId: campaign?.id,
    reportParams: fetchParams.reportParams,
  };
  const { report, loading: loadingReport } = useReport(reportsProps);

  const adSpendHook = useCurrentAdSpend();

  const { value: iroas } = useReturnOnAdSpend({
    campaign: campaign ?? { startDate: '', endDate: '', id: 0 },
    reportDate: report?.reportDate ?? null,
    sales: report?.projectedIncrementalSales ?? null,
    adSpend: adSpendHook.adSpend,
  });

  const { value: roas } = useReturnOnAdSpend({
    campaign: campaign ?? { startDate: '', endDate: '', id: 0 },
    reportDate: report?.reportDate ?? null,
    sales: report?.totalProjectedSales ?? null,
    adSpend: adSpendHook.adSpend,
  });

  const loading = loadingCampaign || loadingReport;

  const { topSalesSubdimensionsByName, topRetailers, subdimensionCounts } =
    useMemo<CalculatedValues>(() => {
      if (!report)
        return {
          subdimensionCounts: {},
          topSalesSubdimensionsByName: {},
          topRetailers: [],
        };

      const allSubdimensionsByName = subdimensionsByName(report.dimensions);

      const getSubdimensionsByNameSorted = topDimensionsByNameSorted(
        allSubdimensionsByName
      );

      const topSalesSubdimensionsByName =
        getSubdimensionsByNameSorted(sortBySalesLift);

      const subdimensionCounts = Object.entries(allSubdimensionsByName).reduce(
        (acc, [dimensionSlug, subdimensions]) => {
          if (!validDimensionSlug(dimensionSlug)) return acc;
          acc[dimensionSlug] = subdimensions.length;
          return acc;
        },
        {} as SubdimensionCounts
      );

      return {
        topSalesSubdimensionsByName,
        topRetailers: getTopRetailers(report.retailers),
        subdimensionCounts,
        error: null,
      };
    }, [report]);

  const roasData: BuildReportOptions['roasData'] = {
    iroas,
    roas,
  };

  if (!campaign || !report) {
    return { error: 'Missing required data for PPT generation', loading };
  }

  return {
    data: {
      campaign,
      report,
      subdimensionCounts,
      roasData,
      topSalesSubdimensionsByName,
      topRetailers,
      retailerCount: report.retailerBanners,
    },
    loading,
  };
};
